import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Avatar,
  LinearProgress,
  Chip,
  Button,
  IconButton,
  Alert,
} from "@mui/material";
import {
  People,
  Business,
  AccessTime,
  Assessment,
  TrendingUp,
  CheckCircle,
  Refresh,
  Notifications,
} from "@mui/icons-material";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { useNavigate } from "react-router-dom";
import { contractorDetailsAPI, analyticsReportsAPI } from "../services/api";
import { calculateProfileCompletion } from "../utils/profileCompletion";
import axios from "axios";

// Sample data for charts
const attendanceData = [
  { name: "Mon", present: 856, absent: 44 },
  { name: "Tue", present: 832, absent: 68 },
  { name: "Wed", present: 878, absent: 22 },
  { name: "Thu", present: 845, absent: 55 },
  { name: "Fri", present: 890, absent: 10 },
  { name: "Sat", present: 923, absent: 7 },
];

const complianceData = [
  { name: "Completed", value: 85, color: "#4caf50" },
  { name: "Pending", value: 12, color: "#ff9800" },
  { name: "Overdue", value: 3, color: "#f44336" },
];

const StatCard = ({ title, value, subtitle, icon, color, trend, onClick }) => (
  <Card
    sx={{
      height: "100%",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: onClick ? "translateY(-4px)" : "none",
        boxShadow: onClick ? 3 : 1,
      },
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <TrendingUp
                sx={{ fontSize: 16, color: "success.main", mr: 0.5 }}
              />
              <Typography variant="caption" color="success.main">
                +{trend}% vs last month
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar
          sx={{
            backgroundColor: color,
            width: 56,
            height: 56,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useAuth();
  const { currentTenant, usage } = useTenant();
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState({
    totalWorkers: 0,
    totalContractors: 0,
    todayAttendance: 0,
    pendingForms: 0,
    loading: true,
  });
  const [notifications, setNotifications] = useState([]);
  const [profileProgress, setProfileProgress] = useState(0);
  const [analyticsData, setAnalyticsData] = useState({
    totalWorkers: 0,
    totalContractors: 0,
    activeReports: 0,
    complianceScore: 0,
    recentActivity: [],
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const updateProfileProgress = useCallback((data) => {
    const { completionPercentage } = calculateProfileCompletion(data);
    setProfileProgress(completionPercentage);
  }, []);

  const loadContractorProfile = useCallback(async () => {
    try {
      // Try to get profile status first
      const response = await contractorDetailsAPI.getProfileStatus();
      if (response.data.success) {
        const profileData = response.data.data;
        updateProfileProgress(profileData);
      } else {
        setProfileProgress(0);
      }
    } catch (error) {
      console.error("Error loading contractor profile:", error);
      // If profile doesn't exist or API fails, set progress to 0
      setProfileProgress(0);
    }
  }, [updateProfileProgress]);

  const loadAnalyticsData = useCallback(async () => {
    try {
      setLoadingAnalytics(true);
      const response = await analyticsReportsAPI.getDashboardOverview();
      setAnalyticsData(response.data.data);
    } catch (error) {
      console.error("Error loading analytics data:", error);
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    loadNotifications();
    loadAnalyticsData();
    if (
      ["contractor_user", "contractor", "contractor_admin"].includes(user?.role)
    ) {
      loadContractorProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, loadContractorProfile, loadAnalyticsData]);

  const loadDashboardData = async () => {
    try {
      const response = await axios.get("/api/dashboard/stats");

      // Parse the response data correctly
      const data = response.data.data || response.data || {};
      setDashboardData({
        totalWorkers: data.totalWorkers || 0,
        totalContractors: data.totalContractors || 0,
        todayAttendance: data.todayAttendance || 0,
        pendingForms: data.pendingForms || 0,
        loading: false,
      });
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      setDashboardData((prev) => ({ ...prev, loading: false }));
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await axios.get("/api/notifications/recent?limit=5");
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to load notifications:", error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  if (dashboardData.loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          {getGreeting()}, {user?.firstName}! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your workforce today
        </Typography>
      </Box>

      {/* Profile Progress Alert for Contractors */}
      {["contractor_user", "contractor", "contractor_admin"].includes(
        user?.role
      ) && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/profile")}
            >
              Complete Profile
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>Profile completion required to activate account.</strong>{" "}
            Complete your profile ({profileProgress}%) to access all features.
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Workers"
            value={
              dashboardData.totalWorkers
                ? dashboardData.totalWorkers.toLocaleString()
                : 0
            }
            subtitle="Active employees"
            icon={<People />}
            color="primary.main"
            trend={8}
          />
        </Grid>
        {!["contractor_user", "contractor", "contractor_admin"].includes(
          user?.role
        ) && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Contractors"
              value={dashboardData.totalContractors || 0}
              subtitle="Registered contractors"
              icon={<Business />}
              color="secondary.main"
              trend={12}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today's Attendance"
            value={`${(
              ((dashboardData.todayAttendance || 0) /
                (dashboardData.totalWorkers || 1)) *
              100
            ).toFixed(1)}%`}
            subtitle={`${dashboardData.todayAttendance || 0} present`}
            icon={<AccessTime />}
            color="success.main"
            trend={5}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Forms"
            value={dashboardData.pendingForms || 0}
            subtitle="Compliance forms"
            icon={<Assessment />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Analytics Overview */}
      {loadingAnalytics ? (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Reports"
              value={analyticsData.activeReports}
              subtitle="Generated reports"
              icon={<Assessment />}
              color="info.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Compliance Score"
              value={`${analyticsData.complianceScore}%`}
              subtitle="Overall compliance"
              icon={<CheckCircle />}
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Workers"
              value={analyticsData.totalWorkers}
              subtitle="From analytics"
              icon={<People />}
              color="primary.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Contractors"
              value={analyticsData.totalContractors}
              subtitle="Active contractors"
              icon={<Business />}
              color="secondary.main"
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Attendance Chart */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Weekly Attendance Overview
              </Typography>
              <IconButton size="small">
                <Refresh />
              </IconButton>
            </Box>
            <ResponsiveContainer width="100%" height="80%">
              <BarChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="present" fill="#4caf50" name="Present" />
                <Bar dataKey="absent" fill="#f44336" name="Absent" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Compliance Status */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Compliance Status
            </Typography>
            <ResponsiveContainer width="100%" height="60%">
              <PieChart>
                <Pie
                  data={complianceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                >
                  {complianceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              {complianceData.map((item) => (
                <Box
                  key={item.name}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: item.color,
                      borderRadius: 1,
                      mr: 2,
                    }}
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {item.value}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Recent Notifications
              </Typography>
              <Button size="small" endIcon={<Notifications />}>
                View All
              </Button>
            </Box>
            <Box>
              {notifications.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 3 }}
                >
                  No recent notifications
                </Typography>
              ) : (
                notifications.map((notification) => (
                  <Box
                    key={notification._id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      p: 2,
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: "background.default",
                    }}
                  >
                    <Avatar
                      sx={{
                        mr: 2,
                        bgcolor: "primary.main",
                        width: 32,
                        height: 32,
                      }}
                    >
                      <Notifications fontSize="small" />
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, mb: 0.5 }}
                      >
                        {notification.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {notification.message}
                      </Typography>
                    </Box>
                    <Chip
                      label={notification.category}
                      size="small"
                      color={
                        notification.type === "error" ? "error" : "default"
                      }
                      sx={{ ml: 1 }}
                    />
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {!["contractor_user", "contractor", "contractor_admin"].includes(
                user?.role
              ) && (
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<People />}
                    sx={{ justifyContent: "flex-start", p: 2 }}
                    onClick={() => navigate("/workers")}
                  >
                    Add Worker
                  </Button>
                </Grid>
              )}
              {!["contractor_user", "contractor", "contractor_admin"].includes(
                user?.role
              ) && (
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Business />}
                    sx={{ justifyContent: "flex-start", p: 2 }}
                    onClick={() => navigate("/contractors")}
                  >
                    Add Contractor
                  </Button>
                </Grid>
              )}
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Assessment />}
                  sx={{ justifyContent: "flex-start", p: 2 }}
                  onClick={() => navigate("/reports")}
                >
                  Generate Report
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<CheckCircle />}
                  sx={{ justifyContent: "flex-start", p: 2 }}
                  onClick={() => navigate("/compliance")}
                >
                  CLRA Forms
                </Button>
              </Grid>
              {["contractor_user", "contractor", "contractor_admin"].includes(
                user?.role
              ) && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<People />}
                    sx={{ justifyContent: "flex-start", p: 2 }}
                    onClick={() => navigate("/profile")}
                  >
                    Complete Profile ({profileProgress}%)
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Subscription Info */}
      {currentTenant && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Subscription Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Plan: {currentTenant.subscription.plan.toUpperCase()} •
                Expires:{" "}
                {new Date(
                  currentTenant.subscription.expiresAt
                ).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "right" }}>
                {usage && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Workers: {usage.currentWorkers} /{" "}
                      {currentTenant.subscription.maxWorkers}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        (usage.currentWorkers /
                          currentTenant.subscription.maxWorkers) *
                        100
                      }
                      sx={{ mt: 1 }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default Dashboard;
